import {Box, Table, TableContainer, Typography} from "@mui/material";
import React from "react";

// Dense, spreadsheet-style table used by the Criminal Records and Related
// News sections. Dan's ask (2026-09-22): the card-per-record layout spent most
// of the page on chrome; reviewers want to scan results the way they would in
// Excel — one row per fact, gridlines, tight type. The theme's MuiTableCell
// override removes borders, so every line is drawn explicitly here.
const GRID = '#E4E8EC';
const HEAD_BG = '#F4F6F8';
const ZEBRA_BG = '#F7F9FA';

export const reportTableSx = {
    width: '100%',
    borderCollapse: 'collapse',
    border: `1px solid ${GRID}`,
    '& th': {
        fontSize: '10.5px',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#637381',
        bgcolor: HEAD_BG,
        borderBottom: `1px solid ${GRID}`,
        borderRight: `1px solid ${GRID}`,
        px: 1,
        py: 0.75,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        textAlign: 'left',
    },
    '& td': {
        fontSize: '12.5px',
        color: '#212B36',
        borderBottom: `1px solid ${GRID}`,
        borderRight: `1px solid ${GRID}`,
        px: 1,
        py: 0.6,
        lineHeight: 1.35,
        verticalAlign: 'top',
        fontVariantNumeric: 'tabular-nums',
    },
    '& th:last-of-type, & td:last-of-type': { borderRight: 'none' },
    '& tbody:nth-of-type(even) td': { bgcolor: ZEBRA_BG },
} as const;

// Horizontal scroll is for the live view on narrow windows; the PDF render
// lays the page out at content width, so nothing is ever clipped there.
export const ReportTable: React.FC<{ children: React.ReactNode; 'aria-label'?: string }> = ({ children, ...rest }) => (
    <TableContainer sx={{ overflowX: 'auto', borderRadius: 1 }}>
        <Table size="small" sx={reportTableSx} {...rest}>
            {children}
        </Table>
    </TableContainer>
);

// Secondary line inside a cell (statute under a charge, URL under a headline).
export const CellNote: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
    <Typography component="div" sx={{ fontSize: '11px', color: '#637381', lineHeight: 1.35, ...sx }}>
        {children}
    </Typography>
);

export const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ border: `1px solid ${GRID}`, borderRadius: 1, px: 2, py: 1.5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '13px', color: '#637381' }}>{children}</Typography>
    </Box>
);

// Section header shared with the rest of the report (icon in a darkBlue box,
// title, subtitle, trailing count chip). Kept to the established pattern; the
// density change is in the body, not the header.
export const truncate = (text: string | undefined | null, max: number): string => {
    const value = (text ?? '').trim();
    if (value.length <= max) return value;
    return `${value.slice(0, max - 1).trimEnd()}…`;
};

export const isHttpsUrl = (url?: string | null): url is string => /^https:\/\//i.test(url ?? '');
